import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchJSON } from '../lib/api-client';
import { computeSurge, type LiveQuote } from '../lib/surge';
import { stickySupportResistance, type Quote } from '@invest-copilot/shared';

interface Candle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface ChartResponse {
  candles: Candle[];
  meta?: { timezone?: string; previousClose?: number };
}

export interface MiniKLineLevels {
  entry?: number;
  stop?: number;
  t1?: number;
  t2?: number;
  /** 使用者實際買入均價（「我買在哪」），僅我的部位卡傳入。 */
  myCost?: number;
}

function validCandles(candles: Candle[], limit: number): Candle[] {
  return candles
    .filter((c) => [c.open, c.high, c.low, c.close].every((v) => typeof v === 'number' && Number.isFinite(v)))
    .slice(-limit);
}

function buildPath(points: Array<{ x: number; y: number }>): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

/**
 * 把折線依「相對昨收基準」逐段切成紅 / 綠線段（漲紅跌綠，台股慣例）。
 * 同一條線可有多個紅綠段：高於昨收的段落紅、低於的綠；跨越基準時在基準線 y 處插交點拆段。
 *
 * @param points  已換算成 SVG 座標的收盤點（x 遞增）
 * @param baselineY  昨收基準的 y 座標（高於昨收 = y 較小）
 * @returns 一組 { d, up } 子路徑，up=true 紅、false 綠
 */
function buildColoredSegments(
  points: Array<{ x: number; y: number }>,
  baselineY: number,
): Array<{ d: string; up: boolean }> {
  if (points.length < 2) return [];
  const segs: Array<{ d: string; up: boolean }> = [];
  // 浮點容差：點正好落在基準線時不視為跨越，避免插出零長度交點。
  const eps = 0.001;
  const sideOf = (y: number): 1 | -1 | 0 => (y < baselineY - eps ? 1 : y > baselineY + eps ? -1 : 0);

  let cur: Array<{ x: number; y: number }> = [points[0]!];
  let curUp = sideOf(points[0]!.y) >= 0; // 落在基準上視為漲（紅）

  const flush = () => {
    if (cur.length >= 2) segs.push({ d: buildPath(cur), up: curUp });
  };

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const p = points[i]!;
    const prevSide = sideOf(prev.y);
    const side = sideOf(p.y);
    // 兩端在基準異側 → 在基準線 y 處插交點，拆段
    if (prevSide !== 0 && side !== 0 && prevSide !== side) {
      const t = (baselineY - prev.y) / (p.y - prev.y); // y 線性內插求交點比例
      const cx = prev.x + (p.x - prev.x) * t;
      const cross = { x: cx, y: baselineY };
      cur.push(cross);
      flush();
      cur = [cross, p];
      curUp = side > 0;
    } else {
      cur.push(p);
      // 從「貼基準」狀態離開時，用新點的方向定調本段顏色
      if (prevSide === 0 && side !== 0 && cur.length === 2) curUp = side > 0;
    }
  }
  flush();
  return segs;
}


function isTaiwanSymbol(symbol: string): boolean {
  return symbol.endsWith('.TW') || symbol.endsWith('.TWO');
}

function fmtPrice(n: number, digits: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtTime(unixSec: number, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(new Date(unixSec * 1000));
  } catch {
    return new Intl.DateTimeFormat('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(unixSec * 1000));
  }
}

/** 量測容器寬度，讓圖能填滿卡片寬（解決固定 viewBox 留白問題）。 */
function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(320);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      // functional update：避免 closure 拿到初始 width，導致縮小時無法往下追蹤
      if (w) setWidth((prev) => (Math.abs(w - prev) > 1 ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, width];
}

const LEVEL_META: Array<{ key: keyof MiniKLineLevels; label: string; tone: 'entry' | 'stop' | 'target' | 'cost' }> = [
  { key: 'myCost', label: '成本', tone: 'cost' },
  { key: 'entry', label: '進', tone: 'entry' },
  { key: 'stop', label: '停', tone: 'stop' },
  { key: 't1', label: 'T1', tone: 'target' },
  { key: 't2', label: 'T2', tone: 'target' },
];

const TONE_COLOR: Record<'entry' | 'stop' | 'target' | 'cost', string> = {
  entry: 'var(--accent)',
  stop: 'var(--down)',
  target: 'var(--up)',
  cost: 'var(--warn)',
};

export function MiniKLine({ symbol, className = '', levels, onLive }: { symbol: string; className?: string; levels?: MiniKLineLevels; onLive?: (live: LiveQuote) => void }) {
  const [wrapRef, width] = useContainerWidth();
  // 台股報價最小跳動到 0.5（1 位小數即可）；美股 2 位小數。
  const priceDigits = isTaiwanSymbol(symbol) ? 1 : 2;

  const oneMin = useQuery({
    queryKey: ['mini-line', symbol, '1m'],
    queryFn: () => fetchJSON<ChartResponse>(`/api/chart/${encodeURIComponent(symbol)}?range=1d&interval=1m`),
    enabled: !!symbol,
    // 急漲 / 急跌要「很快偵測」→ 30 秒刷新（後端 1m 圖 max-age 僅 3 秒，不會吃到舊快取）。
    staleTime: 25_000,
    refetchInterval: 30_000,
    retry: 1,
  });

  const oneMinCount = oneMin.data?.candles?.length ?? 0;
  const fiveMin = useQuery({
    queryKey: ['mini-line', symbol, '5m-fallback'],
    queryFn: () => fetchJSON<ChartResponse>(`/api/chart/${encodeURIComponent(symbol)}?range=5d&interval=5m`),
    enabled: !!symbol && (oneMin.isError || (!oneMin.isLoading && oneMinCount < 2)),
    staleTime: 3 * 60_000,
    refetchInterval: 3 * 60_000,
    retry: 1,
  });

  const useOneMin = oneMinCount >= 2;
  const source = useOneMin ? '1分' : '5分備援';
  const resp = useOneMin ? oneMin.data : fiveMin.data;
  const raw = resp?.candles ?? [];

  // 即時報價（台股=後端 NAS bridge 富邦/TWSE 即時；美股=Yahoo 即時）。
  // /api/quote 對兩個市場都回「該市場當下最即時」的價，故無條件 overlay 到最後一根 K，
  // 美股 overlay Yahoo 即時價到 Yahoo K 棒＝同源無害；台股則把 Yahoo 較舊的 K 棒尾端拉到富邦即時。
  const quoteQ = useQuery({
    queryKey: ['mini-quote', symbol],
    queryFn: () => fetchJSON<Quote>(`/api/quote/${encodeURIComponent(symbol)}`),
    enabled: !!symbol,
    staleTime: 4_000,
    refetchInterval: 5_000,
    retry: 1,
  });
  // 時間軸一律以台灣時間呈現（不分台股 / 美股），與主圖 inferTimezone 一致。
  const timeZone = 'Asia/Taipei';
  // 短線聚焦：1 分 K 只看最近 120 根（=2 小時）；5 分備援只看最近 24 根（=2 小時）。
  // user 反饋：「迷你 K 線時間區間太長，短線看不出來波動」。
  const rawCandles = validCandles(raw, useOneMin ? 120 : 24);

  // 即時價 overlay（台股=富邦/TWSE、美股=Yahoo，都是該市場最即時來源）：把 /api/quote 的最新價
  // 疊到最後一根 K 的 close（high/low 取 max/min），讓 last/changePct/surge 都吃即時價，不再停在
  // Yahoo 1m K 較舊值。quote 無效時原樣用 chart candles（向後相容、美股同源無害）。
  // ⚠️ 用 useMemo 穩定 identity：candles 進了下方 useEffect 的 deps，若每 render 新建陣列
  //    會造成「Maximum update depth exceeded」無限迴圈（2026-06-12 截圖驗證抓到並修）。
  const liveQuotePrice = quoteQ.data?.price;
  const lastRawTs = rawCandles.length > 0 ? rawCandles[rawCandles.length - 1]!.timestamp : 0;
  const candles = useMemo(() => {
    if (rawCandles.length === 0 || typeof liveQuotePrice !== 'number' || !Number.isFinite(liveQuotePrice) || liveQuotePrice <= 0) {
      return rawCandles;
    }
    const out = rawCandles.slice();
    const lastIdx = out.length - 1;
    const lastC = out[lastIdx]!;
    out[lastIdx] = {
      ...lastC,
      close: liveQuotePrice,
      high: Math.max(lastC.high, liveQuotePrice),
      low: Math.min(lastC.low, liveQuotePrice),
    };
    return out;
    // rawCandles 的 identity 隨 query 結果變；用「長度 + 最後一根 ts + 即時價」當穩定依賴鍵，
    // 避免 react-query 每次回傳新陣列 reference 就重算（內容沒變時 candles identity 維持）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawCandles.length, lastRawTs, liveQuotePrice]);

  // 即時報價回報（L1 即時層）：算當日漲跌幅 + 近 5 分鐘速度 + 急漲/急跌旗標，回傳給卡片。
  // 放在 early return 之前以符合 hooks 規則；資料不足時不回報。
  // onLive 以 ref 持有，避免呼叫端傳 inline function（每 render 新 identity）造成 effect 無限重跑。
  const livePrevClose = quoteQ.data?.prevClose;
  const onLiveRef = useRef(onLive);
  onLiveRef.current = onLive;
  useEffect(() => {
    const cb = onLiveRef.current;
    if (!cb) return;
    // surge 也吃 overlay 後的 candles + quote 昨收（即時急漲跌偵測與顯示一致）
    const live = computeSurge(candles, livePrevClose ?? resp?.meta?.previousClose, symbol);
    if (live) cb(live);
  }, [candles, livePrevClose, resp, symbol]);

  const height = 108;

  if (oneMin.isLoading && (fiveMin.isLoading || fiveMin.isPending)) {
    return <div ref={wrapRef} className={`h-27 rounded border border-(--border) bg-(--bg-base) skeleton ${className}`} aria-label={`${symbol} 盤中折線載入中`} />;
  }

  if (candles.length < 2) {
    return (
      <div ref={wrapRef} className={`h-27 rounded border border-(--border) bg-(--bg-base) px-2 py-1 text-[10px] text-(--text-muted) flex items-center justify-center ${className}`}>
        盤中資料不足
      </div>
    );
  }

  const closes = candles.map((c) => c.close);
  const last = candles[candles.length - 1]!;
  const first = candles[0]!;
  // 漲跌幅以「昨收」為基準，跳空 / 漲跌停鎖死時 intraday 首末根會誤判為 0%（例：緯創漲停整天平盤線）。
  // 優先用 /api/quote 的即時昨收（台股富邦 / 美股 Yahoo），次之 chart meta，最後退回首根 close。
  const prevClose = livePrevClose ?? resp?.meta?.previousClose;
  const refClose = typeof prevClose === 'number' && Number.isFinite(prevClose) && prevClose > 0 ? prevClose : first.close;
  const upTrend = last.close >= refClose;
  const stroke = upTrend ? 'var(--up)' : 'var(--down)';
  const changePct = ((last.close - refClose) / refClose) * 100;
  // 台股 ±10% 漲跌停：當日漲跌幅貼著 ±10% 即視為鎖死/觸及，標記提示（避免平盤線被誤讀成資料錯誤）。
  // 美股無漲跌停制度，即使單日 +9.6% 也不能標「漲停」。
  const limitBadge = isTaiwanSymbol(symbol) ? (changePct >= 9.6 ? '漲停' : changePct <= -9.6 ? '跌停' : null) : null;

  // y 軸範圍：只用 candles 收盤算 domain，**不**把昨收 (refClose) 也不把 levels 塞進來，
  // 否則大漲 / 大跌時昨收離現價太遠（例：+7.66% 時昨收 26.1 vs 現價 28.1）會把 Y 軸擐爆，
  // 兩小時的盤中波動被擠到上半部縮成一條平線。昨收 / levels 改成「在範圍內畫線、超出在邊緣標箭頭」。
  const activeLevels = LEVEL_META
    .map((m) => ({ ...m, value: levels?.[m.key] }))
    .filter((m): m is typeof m & { value: number } => typeof m.value === 'number' && Number.isFinite(m.value));
  const candleDomain = closes;
  let min = Math.min(...candleDomain);
  let max = Math.max(...candleDomain);
  if (max <= min) { max = min + 1; }
  const padY = (max - min) * 0.15;
  min -= padY;
  max += padY;

  // 版面：右側留價格刻度，下方留時間軸
  const rightGutter = 46;
  const bottomAxis = 14;
  const topPad = 4;
  const plotW = Math.max(40, width - rightGutter);
  const plotH = height - bottomAxis - topPad;
  const yOf = (v: number) => topPad + ((max - v) / (max - min)) * plotH;
  const step = plotW / Math.max(1, candles.length - 1);
  const points = candles.map((c, i) => ({ x: i * step, y: yOf(c.close) }));
  const lastPoint = points[points.length - 1]!;
  const baselineY = yOf(first.close);

  // Swing pivot 支撐壓力（L1 技術層）：取現價上/下方最近各 1 條畫在折線底層，只畫 Y domain 內的。
  // 用「突破確認遲滞」分類角色（觸及不翻、確實突破才翻），與大 K 線 / 技術帶一致。
  const sr = candles.length >= 8
    ? stickySupportResistance(candles, last.close, 1)
    : { resistance: [], support: [] };
  const srLines = [
    ...sr.resistance.map((l) => ({ key: `sr-r-${l.price}`, price: l.price, label: '壓', up: true })),
    ...sr.support.map((l) => ({ key: `sr-s-${l.price}`, price: l.price, label: '撐', up: false })),
  ].filter((l) => l.price >= min && l.price <= max);

  // 右側價格刻度（最高 / 現價 / 最低）
  const axisTicks = [
    { v: max - padY, kind: 'plain' as const },
    { v: last.close, kind: 'last' as const },
    { v: min + padY, kind: 'plain' as const },
  ];

  const startTime = fmtTime(first.timestamp, timeZone);
  const midIdx = Math.floor(candles.length / 2);
  const midTime = fmtTime(candles[midIdx]!.timestamp, timeZone);
  const endTime = fmtTime(last.timestamp, timeZone);

  return (
    <div ref={wrapRef} className={`min-w-0 w-full rounded border border-(--border) bg-(--bg-base) px-1.5 pt-1 pb-0.5 ${className}`} title={`${symbol} 盤中 ${source} 折線`}>
      {/* max-w-full：SVG 的 width 屬性原本會變成父層 min-content，鎖住 grid 無法縮小造成右側溢出；限制後 min-content 貢獻歸零，ResizeObserver 才能把寬度往下收 */}
      <svg width={width} height={height} role="img" aria-label={`${symbol} 盤中 ${source} 折線`} className="block overflow-visible max-w-full">
        {/* 開盤基準線 */}
        <line x1="0" y1={baselineY} x2={plotW} y2={baselineY} stroke="rgba(148,163,184,.22)" strokeDasharray="3 3" />

        {/* Swing 支撐壓力（最近各 1 條，畫在折線底層、淡虛線 + 右側小標籤） */}
        {srLines.map((l) => {
          const y = yOf(l.price);
          const color = l.up ? 'rgba(239,83,80,.5)' : 'rgba(38,166,154,.5)';
          return (
            <g key={l.key}>
              <line x1="0" y1={y} x2={plotW} y2={y} stroke={color} strokeWidth="0.75" strokeDasharray="1 2" />
              <text x={plotW - 1} y={y - 1.5} textAnchor="end" fill={color} fontSize="7.5" fontWeight="800">{l.label} {fmtPrice(l.price, priceDigits)}</text>
            </g>
          );
        })}

        {/* 參考價位線（進 / 停 / T1 / T2）：
            在 Y 軸 domain 內畫水平線；超出 (例：T2 比現價高 +15%) 改在圖邊標箭頭 + 距離 % */}
        {activeLevels.map((l) => {
          const inRange = l.value >= min && l.value <= max;
          const color = TONE_COLOR[l.tone];
          if (inRange) {
            const y = yOf(l.value);
            return (
              <g key={l.key}>
                <line x1="0" y1={y} x2={plotW} y2={y} stroke={color} strokeWidth="0.75" strokeDasharray="2 3" opacity="0.7" />
                <text x="1" y={y - 1.5} fill={color} fontSize="8" fontWeight="700">{l.label} {fmtPrice(l.value, priceDigits)}</text>
              </g>
            );
          }
          // 超出 domain：在頂部 (level > max) 或底部 (level < min) 標箭頭 + 距現價 %
          const above = l.value > max;
          const y = above ? topPad + 2 : topPad + plotH - 2;
          const distPct = ((l.value - last.close) / last.close) * 100;
          return (
            <text
              key={l.key}
              x="1"
              y={y}
              fill={color}
              fontSize="8"
              fontWeight="700"
              dominantBaseline={above ? 'hanging' : 'auto'}
              opacity="0.85"
            >
              {above ? '↑' : '↓'} {l.label} {fmtPrice(l.value, priceDigits)} ({distPct >= 0 ? '+' : ''}{distPct.toFixed(1)}%)
            </text>
          );
        })}

        {/* 收盤折線：依「相對昨收」逐段漲紅跌綠（同一條線可有多個紅綠段），加粗到 2.6 易讀 */}
        {(() => {
          // 漲跌色基準用昨收 refClose；昨收落在 Y domain 外時 clamp 到圖邊，
          // 讓整段折線維持正確的「全紅 / 全綠」而不是被基準線切錯。
          const baseY = yOf(Math.min(max, Math.max(min, refClose)));
          const segs = buildColoredSegments(points, baseY);
          return segs.map((s, i) => (
            <path
              key={i}
              d={s.d}
              fill="none"
              stroke={s.up ? 'var(--up)' : 'var(--down)'}
              strokeWidth="2.6"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ));
        })()}
        <circle cx={lastPoint.x} cy={lastPoint.y} r="2.6" fill={stroke} />

        {/* 右側價格刻度 */}
        {axisTicks.map((t, i) => {
          const y = Math.min(height - bottomAxis - 1, Math.max(8, yOf(t.v)));
          if (t.kind === 'last') {
            return (
              <g key={i}>
                <rect x={plotW + 2} y={y - 6} width={rightGutter - 3} height="12" rx="2" fill={stroke} />
                <text x={plotW + rightGutter / 2} y={y + 3} textAnchor="middle" fill="var(--bg-base)" fontSize="8.5" fontWeight="800">{fmtPrice(t.v, priceDigits)}</text>
              </g>
            );
          }
          return (
            <text key={i} x={plotW + 3} y={y + 3} fill="var(--text-muted)" fontSize="8">{fmtPrice(t.v, priceDigits)}</text>
          );
        })}

        {/* 漲跌幅 */}
        <text x={plotW - 1} y="9" textAnchor="end" fill={stroke} fontSize="9" fontWeight="700">
          {upTrend ? '▲' : '▼'} {changePct.toFixed(2)}%
        </text>

        {/* 漲停 / 跌停標記 */}
        {limitBadge && (
          <g>
            <rect x="0" y="2" width="26" height="12" rx="2" fill={stroke} />
            <text x="13" y="11" textAnchor="middle" fill="var(--bg-base)" fontSize="8.5" fontWeight="800">{limitBadge}</text>
          </g>
        )}

        {/* 時間軸 */}
        <text x="0" y={height - 2} fill="var(--text-muted)" fontSize="8">{startTime}</text>
        <text x={plotW / 2} y={height - 2} textAnchor="middle" fill="var(--text-muted)" fontSize="8">{midTime}</text>
        <text x={plotW} y={height - 2} textAnchor="end" fill="var(--text-muted)" fontSize="8">{endTime}</text>

        {/* 資料來源 */}
        <text x={plotW + 3} y={height - 2} fill="var(--text-muted)" fontSize="7.5">{source}</text>
      </svg>
    </div>
  );
}
